/**
 * Formats a phone number to E.164 standard format
 * E.164 format: +[country code][national number]
 * Examples: +12345678901, +1234567890
 */
export const formatToE164 = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) {
    return "--";
  }

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If no digits, return default
  if (!digitsOnly) {
    return "--";
  }

  // If already starts with country code (10+ digits), format as E.164
  if (digitsOnly.length >= 10) {
    // If it doesn't start with a country code, assume US/Canada (+1)
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
    // If it's 11 digits and starts with 1, it's already US/Canada format
    else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }
    // For other lengths, assume it already includes country code
    else if (digitsOnly.length > 11) {
      return `+${digitsOnly}`;
    }
    // For 11 digits not starting with 1, assume it includes country code
    else {
      return `+${digitsOnly}`;
    }
  }

  // For shorter numbers, return as-is with + prefix (might be partial)
  return `+${digitsOnly}`;
};

/**
 * Validates if a string is a valid E.164 phone number
 */
export const isValidE164 = (phoneNumber: string): boolean => {
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phoneNumber);
};

/**
 * Formats phone number for display - handles various input formats
 * and converts them to E.164 with visual formatting for better readability
 */
export const formatPhoneForDisplay = (phoneNumber: string | null | undefined): string => {
  const e164 = formatToE164(phoneNumber);
  
  // If it's just "--", return as is
  if (e164 === "--") {
    return e164;
  }
  
  // Format for better visual display
  return formatE164ForDisplay(e164);
};

/**
 * Formats an E.164 phone number for better visual display
 * Following international standards from Google Developer docs and ITU recommendations
 * Examples: 
 * +18097635206 -> +1 809 763 5206
 * +12345678901 -> +1 234 567 8901
 * +5511912345678 -> +55 11 91234 5678 (Brazil mobile)
 * +551112345678 -> +55 11 1234 5678 (Brazil landline)
 * +5255123456789 -> +52 55 1234 5678 (Mexico City)
 * +52664123456789 -> +52 664 123 4567 (Tijuana)
 */
export const formatE164ForDisplay = (e164Number: string): string => {
  if (!e164Number || e164Number === "--" || !e164Number.startsWith('+')) {
    return e164Number;
  }

  // Remove the + sign for processing
  const digits = e164Number.slice(1);
  
  // Handle different country codes based on international standards
  if (digits.startsWith('1') && digits.length === 11) {
    // US/Canada/NANP format: +1 XXX XXX XXXX
    const countryCode = digits.slice(0, 1);
    const areaCode = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const number = digits.slice(7, 11);
    return `+${countryCode} ${areaCode} ${exchange} ${number}`;
  } else if (digits.startsWith('55') && digits.length >= 12) {
    // Brazil format: +55 aa nnnn nnnn (landline) or +55 aa nnnnn nnnn (mobile)
    const countryCode = digits.slice(0, 2);  // 55
    const areaCode = digits.slice(2, 4);     // aa (2 digits)
    const remainingDigits = digits.slice(4);
    
    if (remainingDigits.length === 9) {
      // Mobile: +55 aa nnnnn nnnn (9 digits: 5+4)
      const part1 = remainingDigits.slice(0, 5);
      const part2 = remainingDigits.slice(5, 9);
      return `+${countryCode} ${areaCode} ${part1} ${part2}`;
    } else if (remainingDigits.length === 8) {
      // Landline: +55 aa nnnn nnnn (8 digits: 4+4)
      const part1 = remainingDigits.slice(0, 4);
      const part2 = remainingDigits.slice(4, 8);
      return `+${countryCode} ${areaCode} ${part1} ${part2}`;
    } else {
      // Fallback for irregular Brazil numbers
      return `+${countryCode} ${areaCode} ${remainingDigits}`;
    }
  } else if (digits.startsWith('44') && digits.length >= 10) {
    // UK format: +44 XXXX XXX XXX
    const countryCode = digits.slice(0, 2);
    const remainingDigits = digits.slice(2);
    
    if (remainingDigits.length === 10) {
      // Standard UK mobile/landline
      const part1 = remainingDigits.slice(0, 4);
      const part2 = remainingDigits.slice(4, 7);
      const part3 = remainingDigits.slice(7, 10);
      return `+${countryCode} ${part1} ${part2} ${part3}`;
    } else {
      // Fallback for other UK formats
      const groups = [];
      for (let i = 0; i < remainingDigits.length; i += 3) {
        groups.push(remainingDigits.slice(i, i + 3));
      }
      return `+${countryCode} ${groups.join(' ')}`;
    }
  } else if (digits.startsWith('49') && digits.length >= 11) {
    // Germany format: +49 XXX XXX XXXX
    const countryCode = digits.slice(0, 2);
    const remainingDigits = digits.slice(2);
    const groups = [];
    
    // Group in 3s for better readability
    for (let i = 0; i < remainingDigits.length; i += 3) {
      groups.push(remainingDigits.slice(i, i + 3));
    }
    return `+${countryCode} ${groups.join(' ')}`;
  } else if (digits.startsWith('33') && digits.length >= 10) {
    // France format: +33 X XX XX XX XX
    const countryCode = digits.slice(0, 2);
    const remainingDigits = digits.slice(2);
    
    if (remainingDigits.length === 9) {
      const part1 = remainingDigits.slice(0, 1);
      const part2 = remainingDigits.slice(1, 3);
      const part3 = remainingDigits.slice(3, 5);
      const part4 = remainingDigits.slice(5, 7);
      const part5 = remainingDigits.slice(7, 9);
      return `+${countryCode} ${part1} ${part2} ${part3} ${part4} ${part5}`;
    } else {
      // Fallback
      const groups = [];
      for (let i = 0; i < remainingDigits.length; i += 2) {
        groups.push(remainingDigits.slice(i, i + 2));
      }
      return `+${countryCode} ${groups.join(' ')}`;
    }
  } else if (digits.startsWith('34') && digits.length >= 11) {
    // Spain format: +34 XXX XXX XXX
    const countryCode = digits.slice(0, 2);
    const remainingDigits = digits.slice(2);
    const groups = [];
    
    for (let i = 0; i < remainingDigits.length; i += 3) {
      groups.push(remainingDigits.slice(i, i + 3));
    }
    return `+${countryCode} ${groups.join(' ')}`;
  } else if (digits.startsWith('52') && digits.length >= 12) {
    // Mexico format: +52 AA NNNN NNNN (10-digit national number)
    const countryCode = digits.slice(0, 2);  // 52
    const nationalNumber = digits.slice(2);  // 10 digits: area code + subscriber number
    
    if (nationalNumber.length === 10) {
      // Determine area code length (2 or 3 digits)
      // Major cities (Mexico City: 55, Guadalajara: 33, Monterrey: 81) use 2-digit area codes
      // Other cities use 3-digit area codes
      let areaCodeLength = 3; // Default to 3-digit area code
      
      // Check for 2-digit area codes (major metropolitan areas)
      const firstTwoDigits = nationalNumber.slice(0, 2);
      if (['55', '56', '33', '81'].includes(firstTwoDigits)) {
        areaCodeLength = 2;
      }
      
      const areaCode = nationalNumber.slice(0, areaCodeLength);
      const subscriberNumber = nationalNumber.slice(areaCodeLength);
      
      if (areaCodeLength === 2) {
        // 2-digit area code: +52 AA NNNN NNNN (8-digit subscriber number: 4+4)
        const part1 = subscriberNumber.slice(0, 4);
        const part2 = subscriberNumber.slice(4, 8);
        return `+${countryCode} ${areaCode} ${part1} ${part2}`;
      } else {
        // 3-digit area code: +52 AAA NNN NNNN (7-digit subscriber number: 3+4)
        const part1 = subscriberNumber.slice(0, 3);
        const part2 = subscriberNumber.slice(3, 7);
        return `+${countryCode} ${areaCode} ${part1} ${part2}`;
      }
    } else {
      // Fallback for irregular Mexico numbers
      return `+${countryCode} ${nationalNumber}`;
    }
  } else if (digits.startsWith('91') && digits.length >= 12) {
    // India format: +91 XXXXX XXXXX
    const countryCode = digits.slice(0, 2);
    const remainingDigits = digits.slice(2);
    
    if (remainingDigits.length === 10) {
      const part1 = remainingDigits.slice(0, 5);
      const part2 = remainingDigits.slice(5, 10);
      return `+${countryCode} ${part1} ${part2}`;
    } else {
      // Fallback
      const groups = [];
      for (let i = 0; i < remainingDigits.length; i += 3) {
        groups.push(remainingDigits.slice(i, i + 3));
      }
      return `+${countryCode} ${groups.join(' ')}`;
    }
  } else {
    // Generic format for other countries
    // Determine country code length based on common patterns
    let countryCodeLength = 1;
    
    // Common country code patterns
    if (digits.length >= 10) {
      if (digits.startsWith('7') || digits.startsWith('8') || digits.startsWith('9')) {
        countryCodeLength = 1;
      } else if (digits.length >= 11 && (
        digits.startsWith('20') || digits.startsWith('27') || digits.startsWith('30') ||
        digits.startsWith('31') || digits.startsWith('32') || digits.startsWith('36') ||
        digits.startsWith('39') || digits.startsWith('41') || digits.startsWith('43') ||
        digits.startsWith('45') || digits.startsWith('46') || digits.startsWith('47') ||
        digits.startsWith('48') || digits.startsWith('51') || digits.startsWith('52') ||
        digits.startsWith('53') || digits.startsWith('54') || digits.startsWith('56') ||
        digits.startsWith('57') || digits.startsWith('58') || digits.startsWith('60') ||
        digits.startsWith('61') || digits.startsWith('62') || digits.startsWith('63') ||
        digits.startsWith('64') || digits.startsWith('65') || digits.startsWith('66') ||
        digits.startsWith('81') || digits.startsWith('82') || digits.startsWith('84') ||
        digits.startsWith('86') || digits.startsWith('90') || digits.startsWith('92') ||
        digits.startsWith('93') || digits.startsWith('94') || digits.startsWith('95') ||
        digits.startsWith('98')
      )) {
        countryCodeLength = 2;
      } else if (digits.length >= 12) {
        countryCodeLength = 3;
      }
    }
    
    const countryCode = digits.slice(0, countryCodeLength);
    const remainingDigits = digits.slice(countryCodeLength);
    
    // Format remaining digits in groups of 3 for better readability
    const groups = [];
    for (let i = 0; i < remainingDigits.length; i += 3) {
      groups.push(remainingDigits.slice(i, i + 3));
    }
    
    return `+${countryCode} ${groups.join(' ')}`;
  }
}; 